import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { getApiOrigin } from "../../utils/api";

// Template150: Menú/Plan de viaje de París
// Visually identical to guia-paris/src/components/Page2.jsx
const Template150 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onNavigateToPage,
	pages = [],
	prebuiltGuideId,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const addAuthToken = (url) => {
		if (!url) return url;
		const t = localStorage.getItem("token");
		if (!t) return url;
		try {
			const u = new URL(url, window.location.origin);
			u.searchParams.set("token", t);
			return u.toString();
		} catch {
			return url;
		}
	};

	const resolveImage = (path) => {
		if (!path || typeof path !== "string") return path;
		if (path.startsWith("data:")) return path;
		if (path.startsWith("http")) return addAuthToken(path);
		if (prebuiltGuideId && path.startsWith("/images")) {
			const p = path.startsWith("/") ? path.slice(1) : path;
			return addAuthToken(
				`${getApiOrigin()}/api/prebuilt-guides/${prebuiltGuideId}/assets/${p}`,
			);
		}
		return path;
	};

	// Datos hardcodeados de París (valores por defecto) — matching original Page2.jsx

	// Mapeo de días a templateId (para encontrar la página correspondiente)
	const diasTemplateMapping = [151, 154, 157, 161]; // Île de la Cité, Louvre, Montmartre, Torre Eiffel

	// Mapeo de extras a templateId (4 extras: Metro, Aeropuertos, Actividades, Consejos)
	const extrasTemplateMapping = [164, 165, 166, 167];

	// Función para encontrar una página por templateId
	const findPageByTemplateId = (templateId) => {
		return pages.find(
			(p) => p.templateId === templateId || p.data?.templateId === templateId,
		);
	};

	// Función para obtener el pageId de una página
	const getPageId = (page) => {
		return page?.id || page?.data?.id;
	};

	// Si hay datos en page.data, usarlos; si no, usar los valores por defecto
	const pageData = page.data || {};
	const diasRaw =
		pageData.dias && pageData.dias.length > 0 ? pageData.dias : null;
	const extrasRaw =
		pageData.extras && pageData.extras.length > 0 ? pageData.extras : null;

	// Enriquecer días con pageId basado en templateId
	const dias = diasRaw.map((dia, index) => {
		const templateId = diasTemplateMapping[index];
		const targetPage = templateId ? findPageByTemplateId(templateId) : null;
		const pageId = targetPage ? getPageId(targetPage) : null;
		return {
			...dia,
			pageId: dia.pageId || pageId,
		};
	});

	// Enriquecer extras con pageId basado en templateId
	const extras = extrasRaw
		.map((extra, index) => {
			if (extra.titulo === "Kofi" || extra.image?.includes("kofi")) {
				return null;
			}
			const templateId =
				index >= 0 && index < extrasTemplateMapping.length
					? extrasTemplateMapping[index]
					: null;
			const targetPage = templateId ? findPageByTemplateId(templateId) : null;
			const pageId = targetPage ? getPageId(targetPage) : null;
			return {
				...extra,
				pageId: extra.pageId || pageId,
			};
		})
		.filter(Boolean);

	const actualHeaderColor = headerBackgroundColor || "#2C3E50";
	const headerText = page.texts?.[0]?.content || "Plan de viaje";
	const logoImage = resolveImage(
		page.canvasImages?.find((img) => img.id === "logo")?.imageUrl ||
			"/images/paris/logo-paris.png",
	);

	// Función para navegar
	const handleNavigate = (pageId) => {
		if (onNavigateToPage && pageId) {
			onNavigateToPage(pageId);
		}
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px",
					fontWeight: 600,
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{headerText}
				<Box
					component="img"
					src={logoImage}
					alt="logo"
					sx={{
						width: isSmallScreen ? 66 : 80,
						position: "absolute",
						right: 2,
						top: 18,
						zIndex: 999999,
					}}
				/>
			</Box>

			{/* CONTENIDO */}
			<Box data-block-id="text-1" sx={{ padding: isSmallScreen ? "10px 10px" : "20px 16px" }}>
				{/* DÍAS — columna en móvil y escritorio (uno debajo del otro) */}
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "20px",
						flexWrap: "nowrap",
					}}
				>
					{dias.map((dia, index) => (
						<Box
							key={index}
							onClick={() => {
								if (dia.pageId) {
									handleNavigate(dia.pageId);
								}
							}}
							sx={{
								borderRadius: "12px",
								padding: 0,
								display: "flex",
								alignItems: "center",
								gap: "10px",
								position: "relative",
								minHeight: 30,
								width: "auto",
								cursor: dia.pageId ? "pointer" : "default",
								transition: "transform 0.3s ease",
								marginLeft: "20px",
								"&:hover": dia.pageId
									? {
											transform: "translateY(-5px)",
											"& .box": {
												boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
											},
										}
									: {},
							}}
						>
							{dia.image && (
								<Box
									component="img"
									src={resolveImage(dia.image)}
									alt="logo"
									sx={{
										width: 45,
										zIndex: 100,
										position: "absolute",
										left: -20,
									}}
								/>
							)}
							<Box
								className="box"
								sx={{
									backgroundColor: "#fff",
									borderRadius: "12px",
									padding: "18px 16px",
									display: "flex",
									alignItems: "center",
									gap: "10px",
									position: "relative",
									minHeight: 30,
									width: "100%",
									marginLeft: 0,
									textAlign: "left",
									height: "auto",
									flex: 1,
									transition: "box-shadow 0.3s ease",
									paddingLeft: "16px",
								}}
							>
								<Box
									sx={{
										fontSize: "13px",
										fontWeight: 600,
										color: "#333",
										marginLeft: "20px",
										whiteSpace: "normal",
									}}
								>
									Día {index + 1}: {dia.titulo}
								</Box>
							</Box>
						</Box>
					))}
				</Box>

				{/* SEPARADOR */}
				<Box
					component="h3"
					sx={{
						fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
						fontSize: 12,
						fontWeight: 700,
						margin: 0,
						textTransform: "uppercase",
						marginTop: "16px",
						marginBottom: "16px",
					}}
				>
					Información útil{" "}
				</Box>

				{/* INFORMACIÓN ÚTIL — mismo estilo que los días (icono que sobresale, caja blanca) */}
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "20px",
						flexWrap: "nowrap",
						paddingBottom: "2px",
						overflowY: "visible",
					}}
				>
					{extras.map((extra, index) => (
						<Box
							key={index}
							onClick={() => {
								if (extra.pageId) {
									handleNavigate(extra.pageId);
								}
							}}
							sx={{
								borderRadius: "12px",
								padding: 0,
								display: "flex",
								alignItems: "center",
								gap: "10px",
								position: "relative",
								minHeight: 30,
								width: "auto",
								cursor: extra.pageId ? "pointer" : "default",
								transition: "transform 0.3s ease",
								marginLeft: "20px",
								"&:hover": extra.pageId
									? {
											transform: "translateY(-5px)",
											"& .box": {
												boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
											},
										}
									: {},
							}}
						>
							{extra.image && (
								<Box
									component="img"
									src={resolveImage(extra.image)}
									alt=""
									sx={{
										width: 45,
										zIndex: 100,
										position: "absolute",
										left: -20,
									}}
								/>
							)}
							<Box
								className="box"
								sx={{
									backgroundColor: "#fff",
									borderRadius: "12px",
									padding: "18px 16px",
									display: "flex",
									alignItems: "center",
									gap: "10px",
									position: "relative",
									minHeight: 30,
									width: "100%",
									marginLeft: 0,
									textAlign: "left",
									height: "auto",
									flex: 1,
									transition: "box-shadow 0.3s ease",
									paddingLeft: "16px",
								}}
							>
								<Box
									sx={{
										fontSize: "13px",
										fontWeight: 600,
										color: "#333",
										marginLeft: "20px",
										whiteSpace: "normal",
									}}
								>
									{extra.titulo}
								</Box>
							</Box>
						</Box>
					))}
				</Box>
			</Box>
		</Box>
	);
};

Template150.thumbnail = "/images/template-1.png";

export default Template150;
